/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Conversational Chat Sidebar & Session Management (Task 10.3.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  useChatStore,
  type ChatSessionItem,
  type GroupedSessions,
} from '../../stores/useChatStore';
import { SYSTEM_CHAT_PERSONAS } from '../../data/chat-personas.catalog';
import { getPersonaIcon } from './PersonaSelector';
import { useI18n } from '../../hooks/useI18n';
import {
  Plus,
  Search,
  X,
  Pin,
  PinOff,
  Edit2,
  Trash2,
  MessageSquare,
  Check,
  AlertTriangle,
} from 'lucide-react';

interface ChatSidebarProps {
  onNewChat?: () => void;
  className?: string;
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({
  onNewChat,
  className = '',
}) => {
  const { t } = useI18n();
  const {
    sessions,
    activeSessionId,
    searchQuery,
    selectSession,
    createSession,
    updateSessionTitle,
    togglePinSession,
    deleteSession,
    setSearchQuery,
    getGroupedSessions,
  } = useChatStore();

  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [sessionToDelete, setSessionToDelete] = useState<ChatSessionItem | null>(null);

  const grouped: GroupedSessions = getGroupedSessions();

  const handleStartRename = (session: ChatSessionItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(session.id);
    setEditingTitle(session.title);
  };

  const handleSaveRename = (sessionId: string) => {
    if (editingTitle.trim()) {
      updateSessionTitle(sessionId, editingTitle);
    }
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const handleKeyDownRename = (
    e: React.KeyboardEvent,
    sessionId: string
  ) => {
    if (e.key === 'Enter') {
      handleSaveRename(sessionId);
    } else if (e.key === 'Escape') {
      setEditingSessionId(null);
      setEditingTitle('');
    }
  };

  const handleCreateNewChat = () => {
    createSession();
    if (onNewChat) onNewChat();
  };

  const renderSessionItem = (session: ChatSessionItem) => {
    const isActive = session.id === activeSessionId;
    const persona = SYSTEM_CHAT_PERSONAS.find((p) => p.id === session.personaId);
    const PersonaIcon = persona ? getPersonaIcon(persona.iconName) : MessageSquare;
    const isEditing = editingSessionId === session.id;

    return (
      <div
        key={session.id}
        onClick={() => selectSession(session.id)}
        className={`group relative flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer text-xs transition-all duration-150 ${
          isActive
            ? 'bg-primary/10 text-primary font-semibold border border-primary/25 shadow-sm'
            : 'text-foreground hover:bg-muted/60 border border-transparent'
        }`}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div
            className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
              isActive
                ? 'bg-primary text-primary-fg'
                : 'bg-muted/80 text-foreground-muted group-hover:text-foreground'
            }`}
          >
            <PersonaIcon className="w-3.5 h-3.5" />
          </div>

          {isEditing ? (
            <div
              className="flex items-center gap-1 flex-1 mr-1"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="text"
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                onKeyDown={(e) => handleKeyDownRename(e, session.id)}
                autoFocus
                className="w-full px-2 py-0.5 rounded-lg border border-primary bg-surface text-xs text-foreground focus:outline-none"
              />
              <button
                type="button"
                onClick={() => handleSaveRename(session.id)}
                className="p-1 rounded text-primary hover:bg-primary/10"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setEditingSessionId(null)}
                className="p-1 rounded text-foreground-muted hover:bg-muted"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col min-w-0 flex-1 pr-1">
              <span className="truncate text-xs leading-snug">{session.title}</span>
              <span className="text-[10px] text-foreground-muted truncate font-normal">
                {persona?.name || t('chat.assistantDefaultName')} • {t('chat.messagesCount', { count: session.totalMessages })}
              </span>
            </div>
          )}
        </div>

        {/* Quick action buttons (Pin, Rename, Delete) */}
        {!isEditing && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                togglePinSession(session.id);
              }}
              title={session.isPinned ? t('chat.unpin') : t('chat.pinToTop')}
              className="p-1 rounded-lg text-foreground-muted hover:text-primary hover:bg-muted/80 transition-colors"
            >
              {session.isPinned ? (
                <PinOff className="w-3 h-3 text-accent" />
              ) : (
                <Pin className="w-3 h-3" />
              )}
            </button>
            <button
              type="button"
              onClick={(e) => handleStartRename(session, e)}
              title={t('chat.rename')}
              className="p-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted/80 transition-colors"
            >
              <Edit2 className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSessionToDelete(session);
              }}
              title={t('chat.deleteSession')}
              className="p-1 rounded-lg text-foreground-muted hover:text-destructive hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Small pinned indicator when not hovering */}
        {session.isPinned && !isEditing && (
          <Pin className="w-2.5 h-2.5 text-accent flex-shrink-0 group-hover:hidden ml-1" />
        )}
      </div>
    );
  };

  const renderGroup = (title: string, list: ChatSessionItem[]) => {
    if (list.length === 0) return null;
    return (
      <div className="space-y-1 mb-4">
        <div className="px-3 py-1 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-foreground-muted/70">
          <span>{title}</span>
          <span className="font-mono text-[9px]">{list.length}</span>
        </div>
        <div className="space-y-0.5">{list.map(renderSessionItem)}</div>
      </div>
    );
  };

  return (
    <aside
      className={`w-72 sm:w-80 h-full flex flex-col border-r border-surface-border bg-surface select-none ${className}`}
    >
      {/* Top Header: New Chat Button */}
      <div className="p-3 border-b border-surface-border space-y-2">
        <button
          type="button"
          onClick={handleCreateNewChat}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-fg hover:opacity-95 text-xs font-semibold shadow-sm transition-all active:scale-[0.98]"
        >
          <Plus className="w-4 h-4" />
          <span>{t('chat.newChat')}</span>
        </button>

        {/* Search Bar */}
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-foreground-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('chat.searchChats')}
            className="w-full pl-9 pr-7 py-1.5 rounded-xl border border-surface-border bg-surface-elevated text-xs text-foreground placeholder:text-foreground-muted/60 focus:outline-none focus:ring-1 focus:ring-primary/40 transition-all"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Session Groups Scroll Area */}
      <div className="flex-1 overflow-y-auto p-2 scrollbar-thin">
        {sessions.length === 0 ? (
          <div className="text-center py-10 px-4 space-y-2">
            <MessageSquare className="w-8 h-8 mx-auto text-foreground-muted/40" />
            <p className="text-xs text-foreground-muted">{t('chat.noChatsYet')}</p>
            <button
              type="button"
              onClick={handleCreateNewChat}
              className="text-xs text-primary font-semibold hover:underline"
            >
              {t('chat.startNow')}
            </button>
          </div>
        ) : grouped.pinned.length === 0 &&
          grouped.today.length === 0 &&
          grouped.yesterday.length === 0 &&
          grouped.last7Days.length === 0 &&
          grouped.older.length === 0 ? (
          <div className="text-center py-10 px-4 text-xs text-foreground-muted">
            {t('chat.noMatchingChats')}
          </div>
        ) : (
          <>
            {renderGroup(t('chat.pinned'), grouped.pinned)}
            {renderGroup(t('chat.today'), grouped.today)}
            {renderGroup(t('chat.yesterday'), grouped.yesterday)}
            {renderGroup(t('chat.last7Days'), grouped.last7Days)}
            {renderGroup(t('chat.older'), grouped.older)}
          </>
        )}
      </div>

      {/* Footer statistics */}
      <div className="p-3 border-t border-surface-border bg-surface-elevated/40 flex items-center justify-between text-[11px] text-foreground-muted font-mono">
        <span>{t('chat.totalSessions', { count: sessions.length })}</span>
        <span>Edge D1 Sync</span>
      </div>

      {/* Delete Confirmation Modal */}
      {sessionToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="w-full max-w-sm rounded-2xl border border-surface-border bg-surface p-5 shadow-2xl space-y-4">
            <div className="flex items-center gap-3 text-destructive">
              <div className="w-9 h-9 rounded-xl bg-destructive/15 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <h4 className="font-heading font-bold text-sm text-foreground">
                {t('chat.deleteModalTitle')}
              </h4>
            </div>

            <p className="text-xs text-foreground-muted leading-relaxed">
              {t('chat.deleteModalConfirm')}
            </p>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setSessionToDelete(null)}
                className="px-3 py-1.5 rounded-xl border border-surface-border bg-surface-elevated hover:bg-muted text-xs font-semibold text-foreground transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  deleteSession(sessionToDelete.id);
                  setSessionToDelete(null);
                }}
                className="px-3.5 py-1.5 rounded-xl bg-destructive text-destructive-foreground hover:opacity-90 text-xs font-semibold transition-opacity"
              >
                {t('chat.deleteConfirmBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};
